import type {CaseInput, KernelResult} from "./types";

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function downloadText(filename: string, text: string, type: string) {
  downloadBlob(filename, new Blob([text], {type}));
}

export function resultsToCsv(results: KernelResult[]): string {
  const rows = [["case_id", "projectile", "target", "chemical_sputtering", "tt_ev", "i_tt", "i_tt_over_tt_per_ev", "n_limit_m3", "n_limit_1e19_m3", "alpha_at_n_1e19_m3", "finite_limit"]];
  for (const result of results) for (const p of result.points) rows.push([
    result.case.case_id, result.case.projectile ?? "D", result.case.target, String(result.case.include_chemical_sputtering), String(p.tt_ev), String(p.i_tt),
    String(p.i_tt_over_tt_per_ev), p.n_limit_m3 === null ? "" : String(p.n_limit_m3), p.n_limit_1e19_m3 === null ? "" : String(p.n_limit_1e19_m3),
    String(p.alpha_at_n_1e19_m3), String(p.finite_limit),
  ]);
  return rows.map(row => row.map(value => /[",\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value).join(",")).join("\n") + "\n";
}

function fortranString(value: string) {return `'${value.replaceAll("'", "''")}'`;}
export function caseToNamelist(input: CaseInput): string {
  const lines = ["&pwso0d"];
  const scalar: [string, string | number | boolean][] = Object.entries(input)
    .filter(([key]) => key !== "custom_material") as [string, string | number | boolean][];
  for (const [key, value] of scalar) {
    const encoded = typeof value === "string" ? fortranString(value) : typeof value === "boolean" ? (value ? ".true." : ".false.") : String(value);
    lines.push(`  ${key} = ${encoded}`);
  }
  if (input.custom_material) {
    const custom = input.custom_material;
    lines.push(`  custom_name = ${fortranString(custom.name)}`);
    lines.push(`  custom_z = ${custom.atomic_number}`);
    lines.push(`  custom_mass_u = ${custom.atomic_mass_u}`);
    lines.push(`  custom_us_ev = ${custom.surface_binding_energy_ev}`);
    lines.push(`  custom_q = ${custom.q}`);
    lines.push(`  custom_w = ${custom.w}`);
    lines.push(`  custom_s = ${custom.s}`);
  }
  lines.push("/");
  return lines.join("\n") + "\n";
}

export function chartImage(svg: SVGSVGElement): SVGSVGElement {
  const copy = svg.cloneNode(true) as SVGSVGElement;
  const ns = "http://www.w3.org/2000/svg";
  copy.setAttribute("xmlns", ns);
  copy.removeAttribute("class");
  copy.querySelectorAll("[data-chart-tooltip]").forEach(el => el.remove());
  const originals = svg.querySelectorAll("text");
  const copies = copy.querySelectorAll("text");
  originals.forEach((original, index) => {
    const target = copies[index]; if (!target) return;
    const style = getComputedStyle(original);
    for (const name of ["font-family", "font-size", "font-weight", "fill"]) target.setAttribute(name, style.getPropertyValue(name));
  });
  const section = svg.closest("section");
  const labels = Array.from(section?.querySelectorAll(".legend button:not(.is-hidden)") ?? []).map(button => ({
    text: button.querySelector("span")?.textContent ?? "",
    color: button.querySelector("line")?.getAttribute("stroke") ?? "#334c55",
  }));
  const width = svg.viewBox.baseVal.width, height = svg.viewBox.baseVal.height;
  const header = 48 + labels.length * 20;
  const group = document.createElementNS(ns, "g");
  group.setAttribute("transform", `translate(0 ${header})`);
  while (copy.firstChild) group.append(copy.firstChild);
  copy.append(group);
  const background = document.createElementNS(ns, "rect");
  background.setAttribute("width", String(width)); background.setAttribute("height", String(header)); background.setAttribute("fill", "#fff");
  copy.prepend(background);
  const addText = (text: string, y: number, color: string, size: number) => {
    const el = document.createElementNS(ns, "text");
    el.setAttribute("x", "16"); el.setAttribute("y", String(y)); el.setAttribute("fill", color);
    el.setAttribute("font-family", "sans-serif"); el.setAttribute("font-size", String(size));
    el.textContent = text; copy.append(el);
  };
  addText(section?.querySelector("h2")?.textContent ?? "PWSO", 22, "#102a33", 16);
  addText(section?.querySelector(".chart-scale")?.textContent ?? "", 40, "#334c55", 11);
  labels.forEach((label, i) => addText(label.text, 60 + 20 * i, label.color, 11));
  copy.setAttribute("width", String(width)); copy.setAttribute("height", String(height + header));
  copy.setAttribute("viewBox", `0 0 ${width} ${height + header}`);
  return copy;
}

export function exportSvg(svg: SVGSVGElement, filename: string) {
  downloadText(filename, new XMLSerializer().serializeToString(chartImage(svg)), "image/svg+xml;charset=utf-8");
}

export async function exportPng(svg: SVGSVGElement, filename: string): Promise<void> {
  const copy = chartImage(svg);
  const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(copy)], {type: "image/svg+xml"}));
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {image.onload = () => resolve(); image.onerror = () => reject(new Error("SVG decode failed")); image.src = url;});
    const canvas = document.createElement("canvas");
    canvas.width = 1600;
    canvas.height = Math.round(1600 * Number(copy.getAttribute("height")) / Number(copy.getAttribute("width")));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas unavailable");
    context.fillStyle = "#fff"; context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error("PNG encoding failed")), "image/png"));
    downloadBlob(filename, blob);
  } finally {URL.revokeObjectURL(url);}
}
